import type { AnyRouter } from '@tanstack/react-router';

import { loadedGtag } from '@/lib/analytics';
import { EN_EAGER_BUNDLE } from '@/lib/i18n/bundles/en-eager';
import { composeRouteTitle } from '@/lib/route-title';

const CLICK_IDS = new Set(['gclid', 'gbraid', 'wbraid']);

const isCampaignParam = (name: string): boolean =>
  name.startsWith('utm_') || CLICK_IDS.has(name);

// Query values elsewhere carry emails, OAuth tokens and typed search text.
const cleanUrl = (url: URL): string => {
  const kept = new URLSearchParams(
    [...url.searchParams].filter(([name]) => isCampaignParam(name)),
  ).toString();
  return `${url.origin}${url.pathname}${kept ? `?${kept}` : ''}`;
};

// A same-origin full load (the auth callback) leaves its whole URL here.
const cleanReferrer = (): string | undefined => {
  try {
    return document.referrer ? cleanUrl(new URL(document.referrer)) : undefined;
  } catch {
    return undefined;
  }
};

// gtag stamps this page on any event sent before the first route resolves.
export const landingPageFields = (): {
  page_location: string;
  page_referrer?: string;
} => {
  const referrer = cleanReferrer();
  return {
    page_location: cleanUrl(new URL(window.location.href)),
    ...(referrer ? { page_referrer: referrer } : {}),
  };
};

// Static titles only, in English: a dynamic label (a strategy name) never leaves the tab.
const englishTitle = (title: string): string =>
  EN_EAGER_BUNDLE.nav[title] ?? title;

export const trackPageViews = (router: AnyRouter): (() => void) => {
  let previous: string | undefined;
  return router.subscribe('onResolved', ({ toLocation }) => {
    const gtag = loadedGtag();
    if (!gtag) return;
    const location = cleanUrl(new URL(toLocation.href, window.location.origin));
    // Typing into a `q` box rewrites the URL; that is not a new page.
    if (location === previous) return;
    const referrer = previous ?? cleanReferrer();
    const fields = {
      page_location: location,
      page_title: composeRouteTitle(
        router.state.matches.map((match) => match.staticData.title),
        englishTitle,
      ),
      ...(referrer ? { page_referrer: referrer } : {}),
    };
    // `set` too: gtag stamps the page on every later event, not only this one.
    gtag('set', fields);
    gtag('event', 'page_view', fields);
    previous = location;
  });
};
